import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
  Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import {
  useGetCompanyByIdQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useLazyGetStatusesByModelQuery,
  useLazyGetStatusesQuery,
  useLazyGetCurrenciesQuery,
} from '../../store/api';
import { extractList } from '../../utils/response';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import { toast } from 'react-toastify';
import { buildCompanyRelationParams, resolveCompanyLogoUrl } from './companyApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('status_id', String(values.status_id));
  fd.append('currency_id', String(values.currency_id));
  fd.append('name', values.name ?? '');
  fd.append('slug', values.slug ?? '');
  fd.append('email', values.email ?? '');
  fd.append('email_forward', values.email_forward ?? '');
  fd.append('address', values.address ?? '');
  fd.append('invoice_url', values.invoice_url ?? '');
  if (values.logoFile instanceof File) {
    fd.append('logo', values.logoFile);
  }
  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);
const currencyOptLabel = (c) => c.name ?? c.code ?? String(c.id);

const CreateCompany = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const fileInputRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState('');
  const [emailForward, setEmailForward] = useState('');
  const [address, setAddress] = useState('');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [existingLogoPreview, setExistingLogoPreview] = useState('');

  const [companyRecord, setCompanyRecord] = useState(null);
  const { errors, setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();

  const [fetchStatusesByModel] = useLazyGetStatusesByModelQuery();
  const [fetchStatusesList] = useLazyGetStatusesQuery();
  const [fetchCurrencies] = useLazyGetCurrenciesQuery();

  const companyStatusTrigger = useCallback(
    () => ({
      unwrap: async () => {
        try {
          return await fetchStatusesByModel('company').unwrap();
        } catch {
          try {
            return await fetchStatusesByModel('Company').unwrap();
          } catch {
            return await fetchStatusesList({ paginate: 0 }).unwrap();
          }
        }
      },
    }),
    [fetchStatusesByModel, fetchStatusesList]
  );

  const statusSelect = useLazySelectOptions(companyStatusTrigger, {
    mapResult: (res) => {
      const statusList = extractList(res);
      return (Array.isArray(statusList) ? statusList : []).map((s) => ({
        id: s.id,
        label: statusLabel(s),
        raw: s,
      }));
    },
  });

  const currencySelect = useLazySelectOptions(fetchCurrencies, {
    args: { paginate: 0 },
    mapResult: (res) => {
      const arr = extractList(res);
      return (Array.isArray(arr) ? arr : []).map((c) => ({
        id: c.id,
        label: currencyOptLabel(c),
        raw: c,
      }));
    },
  });

  const loadStatuses = useCallback(
    () =>
      statusSelect.load().catch(() => {
        toast.error('Could not load statuses');
      }),
    [statusSelect]
  );

  const loadCurrencies = useCallback(
    () =>
      currencySelect.load().catch(() => {
        toast.error('Could not load currencies');
      }),
    [currencySelect]
  );

  const {
    data: companyData,
    isLoading: companyLoading,
    isError: companyError,
    error: companyErr,
  } = useGetCompanyByIdQuery(
    { id: editId, params: buildCompanyRelationParams() },
    { skip: !isEdit || !editId }
  );
  const [createCompany] = useCreateCompanyMutation();
  const [updateCompany] = useUpdateCompanyMutation();
  const loadingPage = isEdit && (companyLoading || !hydrated);

  useEffect(() => {
    if (companyError) {
      toast.error(companyErr?.data?.message || 'Failed to load company');
      navigate('/companies');
    }
  }, [companyError, companyErr, navigate]);

  useEffect(() => {
    if (!isEdit) {
      setCompanyRecord(null);
      setExistingLogoPreview('');
      setHydrated(true);
      return;
    }
    if (!companyData) return;
    const c = companyData?.data ?? companyData;
    if (!c) return;
    setCompanyRecord(c);
    setHydrated(true);
  }, [isEdit, companyData]);

  useEffect(() => {
    if (!isEdit || !companyRecord) return;
    const c = companyRecord;

    setName(c.name ?? '');
    setSlug(c.slug ?? '');
    setEmail(c.email ?? '');
    setEmailForward(c.email_forward ?? '');
    setAddress(c.address ?? '');
    setInvoiceUrl(c.invoice_url ?? '');
    setLogoFile(null);
    setExistingLogoPreview(resolveCompanyLogoUrl(c.logo));

    const st = c.status && typeof c.status === 'object' ? c.status : null;
    const sid = c.status_id ?? st?.id;
    if (sid) {
      if (st) {
        const opt = { id: sid, label: statusLabel(st), raw: st };
        setSelectedStatus(opt);
        statusSelect.setOptions((prev) => (prev.some((o) => o.id === sid) ? prev : [...prev, opt]));
      } else {
        const opt = statusSelect.options.find((o) => o.id === sid);
        setSelectedStatus(opt ?? { id: sid, label: String(sid), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }

    const cur = c.currency && typeof c.currency === 'object' ? c.currency : null;
    const cid = c.currency_id ?? cur?.id;
    if (cid) {
      if (cur) {
        const opt = { id: cid, label: currencyOptLabel(cur), raw: cur };
        setSelectedCurrency(opt);
        currencySelect.setOptions((prev) => (prev.some((o) => o.id === cid) ? prev : [...prev, opt]));
      } else {
        const opt = currencySelect.options.find((o) => o.id === cid);
        setSelectedCurrency(opt ?? { id: cid, label: String(cid), raw: {} });
      }
    } else {
      setSelectedCurrency(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [isEdit, companyRecord, statusSelect.options, currencySelect.options]);

  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (f) setLogoFile(f);
    e.target.value = '';
  };

  const clearNewLogo = () => {
    setLogoFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const [logoObjectUrl, setLogoObjectUrl] = useState('');
  useEffect(() => {
    if (!logoFile) {
      setLogoObjectUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const logoPreviewSrc = logoObjectUrl || existingLogoPreview;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailTrim = email.trim();
    const efTrim = emailForward.trim();

    const clientErrors = {};
    if (!name.trim()) clientErrors.name = 'Name is required';
    if (!slug.trim()) clientErrors.slug = 'Slug is required';
    if (!selectedCurrency?.id) clientErrors.currency_id = 'Currency is required';
    if (!selectedStatus?.id) clientErrors.status_id = 'Status is required';
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim))
      clientErrors.email = 'Enter a valid email or leave it empty';
    if (efTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(efTrim))
      clientErrors.email_forward = 'Enter a valid email forward or leave it empty';
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    const values = {
      status_id: selectedStatus.id,
      currency_id: selectedCurrency.id,
      name: name.trim(),
      slug: slug.trim(),
      email: emailTrim,
      email_forward: efTrim,
      address: address.trim(),
      invoice_url: invoiceUrl.trim(),
      logoFile,
    };

    const fd = buildFormData(values);

    setSubmitting(true);
    try {
      // No relations on write requests — the page navigates away from the response
      if (isEdit) {
        await updateCompany({ id: editId, data: fd }).unwrap();
        toast.success('Company updated');
      } else {
        await createCompany({ data: fd }).unwrap();
        toast.success('Company added');
      }
      navigate('/companies');
    } catch (err) {
      applyApiError(err, isEdit ? 'Failed to update company' : 'Failed to add company');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/companies')} sx={{ mb: 2 }}>
          Back to companies
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit company' : 'Add company'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>

              <Grid item xs={12} sm={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError('name');
                  }}
                  required
                  fullWidth
                  {...fieldProps('name')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={currencySelect.options}
                  value={selectedCurrency}
                  onChange={(_, v) => {
                    setSelectedCurrency(v);
                    clearError('currency_id');
                  }}
                  onOpen={loadCurrencies}
                  loading={currencySelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Currency"
                      required
                      placeholder="Select currency"
                      fullWidth
                      {...fieldProps('currency_id')}
                    />
                  )}
                />
              </Grid>



              <Grid item xs={12} sm={6}>
                <TextField
                  label="Slug"
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    clearError('slug');
                  }}
                  required
                  fullWidth
                  {...fieldProps('slug')}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Logo
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                  <Button variant="outlined" component="label" disabled={submitting}>
                    Upload logo
                    <input ref={fileInputRef} hidden type="file" accept="image/*" onChange={onPickLogo} />
                  </Button>
                  {(logoFile || existingLogoPreview) && (
                    <>
                      <Avatar src={logoPreviewSrc || undefined} variant="rounded" sx={{ width: 64, height: 64 }} />
                      {logoFile && (
                        <Button type="button" size="small" onClick={clearNewLogo}>
                          Remove new file
                        </Button>
                      )}
                    </>
                  )}
                </Box>
                {errors.logo && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.logo}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError('email');
                  }}
                  fullWidth
                  {...fieldProps('email')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email forward"
                  type="email"
                  value={emailForward}
                  onChange={(e) => {
                    setEmailForward(e.target.value);
                    clearError('email_forward');
                  }}
                  fullWidth
                  {...fieldProps('email_forward')}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Address"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    clearError('address');
                  }}
                  fullWidth
                  multiline
                  minRows={2}
                  {...fieldProps('address')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Invoice URL"
                  value={invoiceUrl}
                  onChange={(e) => {
                    setInvoiceUrl(e.target.value);
                    clearError('invoice_url');
                  }}
                  fullWidth
                  placeholder="https://..."
                  {...fieldProps('invoice_url')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={statusSelect.options}
                  value={selectedStatus}
                  onChange={(_, v) => {
                    setSelectedStatus(v);
                    clearError('status_id');
                  }}
                  onOpen={loadStatuses}
                  loading={statusSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      required
                      placeholder="Select status"
                      fullWidth
                      {...fieldProps('status_id')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Add company'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => navigate('/companies')} disabled={submitting}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateCompany;
